import { useMemo, useState } from 'react';

import type { ApBillListRow, ApPaymentWithAllocations, CreateApPayment } from '@dcc-erp/shared';
import { dateToInputValue, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { WhtLineEditor, type WhtLineState } from './WhtLineEditor';
import { useCoa } from '../../lib/queries';
import { useApBill, useCreateApPayment } from '../../lib/queries/ap';
import {
  modalBody,
  modalBox,
  modalError,
  modalFooter,
  modalHeader,
  modalLabel,
  modalOverlay,
  modalTitle,
} from '../ar/modal-style';

// =============================================================================
// Modal: Record AP Payment (for a specific bill — single-allocation MVP)
// =============================================================================

interface RecordPaymentModalProps {
  companyId: string;
  bill: ApBillListRow;
  // M7 Phase 1b — createPayment now returns a DRAFT (no JV). Hand the draft up
  // so the route opens ApPaymentApprovalPanel for submit → approve instead of
  // closing as "paid".
  onCreated: (payment: ApPaymentWithAllocations) => void;
  onClose: () => void;
}

export function RecordPaymentModal({
  companyId,
  bill,
  onCreated,
  onClose,
}: RecordPaymentModalProps) {
  const { data: coa } = useCoa(companyId, true);
  // AP Phase C1 — the discount window (inDiscountWindow / discountAmount /
  // discountDueDate) is DERIVED server-side in findBill, so pull the bill detail
  // to know whether the early-payment-discount checkbox should appear. The list
  // row only carries the raw snapshot (discountPercent/discountDays).
  const { data: billDetail } = useApBill(companyId, bill.id);
  const create = useCreateApPayment(companyId);

  // Stable idempotency key — generated ONCE per modal open and reused across
  // every submit/retry in this session. A double-click (or a retry after a
  // flaky network) therefore replays the SAME key, which the server uses to
  // short-circuit to the already-created payment (no duplicate payment / no
  // double-minted 50-ทวิ cert). Do NOT regenerate this per click.
  const [idemKey] = useState(() => crypto.randomUUID());

  const today = new Date();
  const outstanding = toNumber(bill.totalAmount) - toNumber(bill.paidAmount);

  const [paymentDate, setPaymentDate] = useState(dateToInputValue(today));
  const [cashAccountCode, setCashAccountCode] = useState<string>('');
  const [amount, setAmount] = useState<number>(outstanding);
  const [description, setDescription] = useState('');
  // AP Phase C1 — take the bill's early-payment (cash) discount. Eligible only
  // when the bill is in its discount window (findBill.inDiscountWindow) AND this
  // payment settles the FULL outstanding (the discount requires a full settle).
  const [takeDiscount, setTakeDiscount] = useState(false);

  const discountAmount = toNumber(billDetail?.discountAmount);
  const discountPercent = toNumber(billDetail?.discountPercent ?? bill.discountPercent);
  // Full-settle = paying the whole note-adjusted outstanding (2dp tolerance).
  const fullSettle = Math.abs(amount - outstanding) < 0.005;
  const discountEligible =
    billDetail?.inDiscountWindow === true && discountAmount > 0 && fullSettle;
  // The discount can only stay armed while it's eligible — un-arm it otherwise so
  // a part-payment / window-expiry never sends takeDiscount.
  const discountActive = takeDiscount && discountEligible;
  // Cash actually leaving = amount − discount taken (the discount is booked to
  // ส่วนลดรับ, not paid out). WHT (B1) reduces cash further at the service layer.
  const cashOut = discountActive ? amount - discountAmount : amount;
  // B1: WHT state — disabled by default (backward compatible).
  const [wht, setWht] = useState<WhtLineState>({ enabled: false, incomeType: 'SVC', rate: 3 });
  // B5: e-WHT channel — default NORMAL; toggle to E_WHT so the cert is minted
  // with channel=E_WHT and excluded from 50ทวิ/ภ.ง.ด. (bank files those).
  const [whtChannel, setWhtChannel] = useState<'NORMAL' | 'E_WHT'>('NORMAL');

  // Pre-VAT portion of the entered payment amount (used for WHT base preview).
  const subtotalOfPayment = useMemo(() => {
    const total = toNumber(bill.totalAmount);
    const sub = toNumber(bill.subtotal ?? bill.totalAmount);
    return total > 0 ? (amount * sub) / total : 0;
  }, [amount, bill]);

  // Cash + bank accounts — codes in the 1110-1119 range (cash equivalents).
  const cashAccounts = useMemo(() => {
    if (!coa) return [];
    return coa.filter(
      (c) => c.type === 'ASSET' && c.isActive && c.code >= '1110' && c.code < '1120',
    );
  }, [coa]);

  // COA-resolved default: first active cash/bank account; '' until COA loads.
  // Derived value — avoids a useEffect setter (lint: react-hooks/set-state-in-effect).
  const effectiveCashCode =
    cashAccountCode !== '' ? cashAccountCode : (cashAccounts[0]?.code ?? '');

  async function handleSubmit() {
    const payload: CreateApPayment = {
      paymentDate,
      vendorId: bill.vendorId,
      cashAccountCode: effectiveCashCode,
      // M7 Phase 1a — idempotencyKey is now REQUIRED in the body (the server
      // dedups on it to close the double-pay hole; body wins over the legacy
      // header). Stable per modal open (see idemKey) so a double-click / retry
      // replays the SAME key instead of minting a second payment.
      idempotencyKey: idemKey,
      ...(description ? { description } : {}),
      // AP Phase C1 — flag the allocation to take the early-payment discount
      // (only when armed + eligible; the server re-gates the window + full-settle).
      allocations: [{ billId: bill.id, amount, ...(discountActive ? { takeDiscount: true } : {}) }],
      // B1: include WHT instruction when the user has enabled it.
      ...(wht.enabled
        ? { whtLines: [{ billId: bill.id, incomeType: wht.incomeType, rate: wht.rate }] }
        : {}),
      // B5: thread the e-WHT channel so the cert is minted with channel=E_WHT
      // and excluded from 50ทวิ/ภ.ง.ด. (the bank files those directly).
      ...(whtChannel === 'E_WHT' ? { whtChannel: 'E_WHT' as const } : {}),
    };
    // M7 1b — createPayment now returns a DRAFT (no JV, no paidAmount bump).
    // Hand it up so the route opens ApPaymentApprovalPanel for submit → approve,
    // instead of closing the flow as an instant payment.
    const draft = await create.mutateAsync({ data: payload, idempotencyKey: idemKey });
    onCreated(draft);
    onClose();
  }

  const disabled = !effectiveCashCode || amount <= 0 || amount > outstanding || create.isPending;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: 540 }}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>บันทึกการจ่าย — {bill.voucherNo}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={modalBody}>
          <div className="muted" style={{ fontSize: 14 }}>
            ผู้ขาย: <b>{bill.vendorName}</b>
          </div>
          <div className="muted" style={{ fontSize: 14 }}>
            ยอดคงค้าง: <b className="tnum">{fmt(outstanding, 2)}</b> บาท
          </div>

          <div>
            <label style={modalLabel}>วันที่จ่าย</label>
            <input
              type="date"
              className="input"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div>
            <label style={modalLabel}>บัญชีเงินสด / ธนาคาร</label>
            <select
              className="select"
              value={effectiveCashCode}
              onChange={(e) => setCashAccountCode(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              {cashAccounts.length === 0 && <option value="">— ไม่พบบัญชีเงินสด/ธนาคาร —</option>}
              {cashAccounts.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} · {a.nameTh}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={modalLabel}>จำนวนเงิน</label>
            <input
              type="number"
              className="input tnum"
              value={amount}
              min={0}
              max={outstanding}
              step={0.01}
              onChange={(e) => setAmount(Number(e.target.value))}
              style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
            />
          </div>

          {/* AP Phase C1 — early-payment (cash) discount. Shown only when the
              bill is in its discount window AND this payment settles the full
              outstanding. Ticking it arms takeDiscount on the allocation, books
              the discount to ส่วนลดรับ, and lowers the cash actually paid out. */}
          {discountEligible && (
            <div data-testid="discount-block">
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input
                  type="checkbox"
                  aria-label="รับส่วนลดจ่ายเร็ว"
                  checked={takeDiscount}
                  onChange={(e) => setTakeDiscount(e.target.checked)}
                />
                รับส่วนลดจ่ายเร็ว {discountPercent}% (= {fmt(discountAmount, 2)} บาท)
                {billDetail?.discountDueDate ? (
                  <span className="muted"> · ถึง {fmtDate(billDetail.discountDueDate)}</span>
                ) : null}
              </label>
            </div>
          )}

          {/* Cash actually leaving the account — reflects the discount taken. */}
          <div className="muted" style={{ fontSize: 14 }}>
            ยอดจ่ายสุทธิ:{' '}
            <b className="tnum" data-testid="cash-out">
              {fmt(cashOut, 2)}
            </b>{' '}
            บาท
          </div>

          {/* B1: WHT block — tick to enable หัก ณ ที่จ่าย on this payment */}
          <WhtLineEditor
            subtotal={subtotalOfPayment}
            billId={bill.id}
            value={wht}
            onChange={setWht}
          />

          {/* B5: e-WHT channel toggle — mints the cert with channel=E_WHT so it
              is excluded from 50ทวิ/ภ.ง.ด. (the bank files directly). Appears
              only when WHT is enabled. */}
          {wht.enabled && (
            <div data-testid="ewht-block" style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
                <input
                  type="checkbox"
                  aria-label="ชำระแบบ e-WHT"
                  checked={whtChannel === 'E_WHT'}
                  onChange={(e) => setWhtChannel(e.target.checked ? 'E_WHT' : 'NORMAL')}
                />
                ชำระแบบ e-WHT (ธนาคารนำส่ง — ยกเว้นจาก 50ทวิ / ภ.ง.ด.)
              </label>
            </div>
          )}

          <div>
            <label style={modalLabel}>คำอธิบาย (optional)</label>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="หมายเหตุ / เลขที่อ้างอิงเช็ค"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          {create.error && <div style={modalError}>บันทึกไม่สำเร็จ: {create.error.message}</div>}
        </div>
        <div style={modalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} />{' '}
            {create.isPending ? 'กำลังบันทึก…' : 'สร้างรายการจ่าย (รออนุมัติ)'}
          </button>
        </div>
      </div>
    </div>
  );
}
